import { useEffect, useState } from 'react';
import { useT, useLang } from '@/i18n/dgtgLang';
import { PROCESS_STEPS, FLOW_PROMISES } from './dgtgFlow';

// The DGTG process line, in the homepage hero. Six stations on one horizontal track, drawn by hand in SVG
// like an engineering schematic: context, plan, create, distribute, verify, learn, and a return loop from
// learn back to context. Small packets travel the track and the loop; the active station is highlighted
// and a console line under the drawing says what happens there. Hover or tap a station to hold it.
// In Arabic the whole line runs right to left, as the page does; typewriter lettering is kept for Latin
// scripts only. Still for reduced-motion users. On narrow screens the drawing scrolls sideways rather than shrinking.
// Content lives in dgtgFlow.js.

const INK = '#16131f';
const VIOLET = '#773DFF';
const X0 = 112;
const STEP = 175;
const Y = 104;
const R = 34;
const CYCLE_MS = 2600;
const xs = PROCESS_STEPS.map((_, i) => X0 + i * STEP);
const X_END = xs[xs.length - 1];
const RETURN = `C${X_END + 90},${Y} ${X_END + 90},${Y + 118} ${X_END - 20},${Y + 118} H${X0 + 20} C${X0 - 90},${Y + 118} ${X0 - 90},${Y} ${X0 - R - 26},${Y}`;
// The track, then down and back under it to the start: the path the packets follow.
const LOOP = `M${X0 - R - 26},${Y} H${X_END + R + 26} ${RETURN}`;

// Hand-drawn marks, centred on 0,0, about 24 units across. Strokes only, so they take the station colour.
function Glyph({ name, color }) {
  const p = { fill: 'none', stroke: color, strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'signals':
      return <g {...p}><circle cx="-7" cy="7" r="2" fill={color} /><path d="M-7,-1 A8,8 0 0 1 1,7" /><path d="M-7,-8 A15,15 0 0 1 8,7" /><path d="M-7,-15 A22,22 0 0 1 15,7" opacity="0.5" /></g>;
    case 'plan':
      return <g {...p}><rect x="-11" y="-10" width="22" height="20" rx="2" /><path d="M-11,-3 H11 M-4,-10 V10 M4,-3 V10" /><rect x="-3" y="1" width="6" height="8" fill={color} stroke="none" opacity="0.35" /></g>;
    case 'create':
      return <g {...p}><rect x="-11" y="-9" width="16" height="18" rx="2" /><path d="M-7,-3 H1 M-7,1 H-1" /><path d="M3,7 L12,-2 L14,0 L5,9 L2,10 Z" /></g>;
    case 'distribute':
      return <g {...p}><circle cx="-10" cy="0" r="2.4" fill={color} /><path d="M-8,0 H-2 M-2,0 C3,0 3,-9 9,-9 M-2,0 H9 M-2,0 C3,0 3,9 9,9" /><circle cx="11" cy="-9" r="2" /><circle cx="11" cy="0" r="2" /><circle cx="11" cy="9" r="2" /></g>;
    case 'verify':
      return <g {...p}><path d="M0,-12 L9,-8 V0 C9,6 5,10 0,12 C-5,10 -9,6 -9,0 V-8 Z" /><path d="M-4,0 L-1,4 L5,-4" /></g>;
    case 'learn':
      return <g {...p}><path d="M9,-3 A10,10 0 1 0 6,7" /><path d="M9,-10 V-3 H2" /><circle cx="0" cy="0" r="2" fill={color} /></g>;
    default:
      return null;
  }
}

function useReducedMotion() {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    const m = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!m) return undefined;
    setReduce(m.matches);
    const on = (e) => setReduce(e.matches);
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, []);
  return reduce;
}

export function HeroProcess() {
  const t = useT();
  const { lang, dir } = useLang();
  const rtl = dir === 'rtl';
  const latin = !['ar', 'zh'].includes(lang);
  const reduce = useReducedMotion();
  const [active, setActive] = useState(0);
  const [held, setHeld] = useState(false);

  useEffect(() => {
    if (held || reduce) return undefined;
    const id = setTimeout(() => setActive((i) => (i + 1) % PROCESS_STEPS.length), CYCLE_MS);
    return () => clearTimeout(id);
  }, [active, held, reduce]);

  const step = PROCESS_STEPS[active];
  const mono = latin ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit';
  const sx = (x) => (rtl ? 1100 - x : x);   // station position, mirrored for right-to-left
  return (
    <div className="mt-12" data-testid="dgtg-hero-process">
      <div className="overflow-x-auto -mx-6 px-6 pb-2" style={{ scrollbarWidth: 'none' }}>
        <svg viewBox="0 0 1100 250" className="w-full min-w-[720px] h-auto" role="img"
          aria-label={t('The DGTG process: context, plan, create, distribute, verify, learn, and back to context.')}
          onMouseLeave={() => setHeld(false)}>
          <defs>
            <pattern id="dgtg-grid" width="22" height="22" patternUnits="userSpaceOnUse">
              <path d="M22,0 H0 V22" fill="none" stroke="#e7e4ee" strokeWidth="0.8" />
            </pattern>
            <path id="dgtg-loop" d={LOOP} />
          </defs>
          <rect x="0" y="0" width="1100" height="250" fill="url(#dgtg-grid)" rx="14" />

          {/* the track and the return loop (mirrored as a whole for right-to-left) */}
          <g transform={rtl ? 'translate(1100,0) scale(-1,1)' : undefined}>
          <path d={`M${X0 - R - 26},${Y} H${X_END + R + 26}`} stroke={INK} strokeWidth="1.6" />
          <path d={`M${X_END + R + 26},${Y} ${RETURN}`} fill="none" stroke={INK} strokeWidth="1.2" strokeDasharray="2 5" opacity="0.55" />
          <path d={`M${X0 + 150},${Y + 112} l-10,6 l10,6`} fill="none" stroke={INK} strokeWidth="1.2" opacity="0.55" />
          {/* packets travelling the loop */}
          {!reduce && [0, 1.6, 3.2, 4.8].map((b) => (
            <rect key={b} x="-7" y="-2.5" width="14" height="5" rx="2.5" fill={VIOLET}>
              <animateMotion dur="6.4s" begin={`-${b}s`} repeatCount="indefinite" rotate="auto">
                <mpath href="#dgtg-loop" />
              </animateMotion>
            </rect>
          ))}
          </g>
          <text x="550" y={Y + 140} textAnchor="middle" fontFamily={mono} fontSize="12" fill="#6b6780">
            {t('what we learn goes back in')}
          </text>

          {/* stations */}
          {PROCESS_STEPS.map((s, i) => {
            const on = i === active;
            return (
              <g key={s.id} transform={`translate(${sx(xs[i])},${Y})`} className="cursor-pointer"
                onMouseEnter={() => { setActive(i); setHeld(true); }} onClick={() => { setActive(i); setHeld(true); }}>
                {on && !reduce && <circle r={R} fill="none" stroke={VIOLET} strokeWidth="1.2" className="dgtg-ring" />}
                <circle r={R} fill={on ? '#f3edff' : '#fff'} stroke={on ? VIOLET : INK} strokeWidth={on ? 2 : 1.6} />
                <Glyph name={s.glyph} color={on ? VIOLET : INK} />
                <text y={-R - 14} textAnchor="middle" fontFamily={mono} fontSize="12" fill={on ? VIOLET : '#8a8699'}>
                  {String(i + 1).padStart(2, '0')}
                </text>
                <text y={R + 26} textAnchor="middle" fontSize="16" fontWeight="700" fill={on ? VIOLET : INK}>{t(s.title)}</text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* what happens at the active station, as a console line */}
      <div className={`mt-4 flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-[13px] text-gray-700 ${latin ? 'font-mono' : ''}`} aria-live="polite">
        <span className="text-[#773DFF] shrink-0">{String(active + 1).padStart(2, '0')} {t(step.title).toLowerCase()}</span>
        <span className="min-w-0">{t(step.log)}<span className={reduce ? 'text-[#773DFF] ms-0.5' : 'dgtg-caret'}>▍</span></span>
      </div>
      <style>{`
        @keyframes dgtg-ring { from { r: ${R}px; opacity: 0.9; } to { r: ${R + 16}px; opacity: 0; } }
        .dgtg-ring { animation: dgtg-ring 1.6s ease-out infinite; }
        @keyframes dgtg-caret { 50% { opacity: 0; } }
        .dgtg-caret { animation: dgtg-caret 1s steps(1) infinite; color: #773DFF; margin-left: 2px; }
      `}</style>
    </div>
  );
}

// Under the hero: each station in plain words, then why brands pick DGTG.
export function HowWeWorkSection() {
  const t = useT();
  return (
    <section id="how-we-work" className="py-20 bg-white border-b border-gray-100 scroll-mt-16" data-testid="dgtg-how-we-work">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-2xl sm:text-3xl font-black text-gray-900">{t('How DGTG works')}</h2>
        <p className="mt-3 text-lg text-gray-600 max-w-3xl leading-relaxed">
          {t('Our people bring the judgement and more than a decade of campaign data. AI helps them use it faster. The same six steps, on every campaign.')}
        </p>
        <ol className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
          {PROCESS_STEPS.map((s, i) => (
            <li key={s.id}>
              <span className="font-mono text-sm text-[#773DFF]">{String(i + 1).padStart(2, '0')}</span>
              <h3 className="mt-1 text-lg font-bold text-gray-900">{t(s.title)}</h3>
              <p className="mt-2 text-gray-600 leading-relaxed">{t(s.text)}</p>
            </li>
          ))}
        </ol>

        <h2 className="mt-16 text-2xl sm:text-3xl font-black text-gray-900">{t('Why brands pick us')}</h2>
        <div className="mt-8 grid md:grid-cols-3 gap-8">
          {FLOW_PROMISES.map((p) => (
            <div key={p.title} className="border-t-2 border-gray-900 pt-4">
              <h3 className="text-lg font-bold text-gray-900">{t(p.title)}</h3>
              {p.stat && <p className="mt-1 text-3xl font-black text-[#773DFF]">{p.stat}</p>}
              <p className="mt-2 text-gray-600 leading-relaxed">{t(p.text)}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
